/**
 * Reusable badge that surfaces the full data-vintage manifest behind a
 * Clarid analysis output. Lets users see "Census ZCBP 2023, FFIEC A1
 * 2024, HUD 2024Q4" at a glance, and click to expand the full breakdown.
 *
 * Backed by the backend `dataVintage.service.ts` capability surfaced via
 * the SB density query and persisted on CraGapAnalysis records. Bobby +
 * Michael flagged this 2026-05-29 as a CRA reproducibility requirement —
 * board reports and exam packages need to be auditable to the data
 * vintage they were generated against.
 */

import { useState } from 'react';
import { InformationCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface DataVintage {
  censusZcbpYear: number | null;
  censusNesYear: number | null;
  censusTractReferenceYear: number | null;
  ffiecAggregateYear: number | null;
  hudCrosswalkVersion: string | null;
  computedAt: string;
}

interface DataVintageBadgeProps {
  vintage: DataVintage | null | undefined;
  /** Tighter compact rendering for inline placement next to titles */
  compact?: boolean;
  /** Override the heading label (default "Data vintage") */
  label?: string;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

/**
 * Short summary line — drops empty vintages. Used as the badge label and
 * as the inline copy for AI / log surfaces.
 */
function shortDescribe(v: DataVintage): string {
  const parts: string[] = [];
  if (v.censusZcbpYear) parts.push(`ZCBP ${v.censusZcbpYear}`);
  if (v.ffiecAggregateYear) parts.push(`A1 ${v.ffiecAggregateYear}`);
  if (v.hudCrosswalkVersion) parts.push(`HUD ${v.hudCrosswalkVersion}`);
  return parts.length > 0 ? parts.join(' · ') : 'no source data';
}

export default function DataVintageBadge({
  vintage,
  compact = false,
  label = 'Data vintage',
}: DataVintageBadgeProps) {
  const [open, setOpen] = useState(false);

  if (!vintage) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400 italic">
        <InformationCircleIcon className="h-3.5 w-3.5" />
        no vintage captured
      </span>
    );
  }

  const summary = shortDescribe(vintage);

  return (
    <div className={`relative ${compact ? 'inline-block' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors ${
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
        } text-gray-600`}
        title="Show the reference-data versions backing this view"
      >
        <InformationCircleIcon className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} text-gray-500`} />
        <span className="font-mono">{summary}</span>
        <ChevronDownIcon
          className={`${compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <>
          {/* Click-away overlay so popups dismiss on outside click */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 z-20 mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{label}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Reference-data versions used by this analysis
              </p>
            </div>
            <dl className="px-4 py-3 space-y-2">
              <VintageRow
                label="Census ZCBP"
                value={vintage.censusZcbpYear ? String(vintage.censusZcbpYear) : null}
                hint="ZIP Code Business Patterns — employer establishment counts"
              />
              <VintageRow
                label="Census NES"
                value={vintage.censusNesYear ? String(vintage.censusNesYear) : null}
                hint="Nonemployer Statistics — sole proprietor counts"
              />
              <VintageRow
                label="FFIEC Census"
                value={vintage.censusTractReferenceYear ? String(vintage.censusTractReferenceYear) : null}
                hint="Tract demographics (income, population, minority composition)"
              />
              <VintageRow
                label="FFIEC CRA Aggregate A1"
                value={vintage.ffiecAggregateYear ? String(vintage.ffiecAggregateYear) : null}
                hint="Originations A1-1 + purchases A1-2 — bank lending activity"
              />
              <VintageRow
                label="HUD ZIP-to-Tract"
                value={vintage.hudCrosswalkVersion ?? null}
                hint="Quarterly crosswalk used for ZIP-to-tract allocation"
              />
            </dl>
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
              <p className="text-[10px] text-gray-500">
                Captured {formatTimestamp(vintage.computedAt)}. Use this manifest to
                reproduce the analysis later — the same versions of these
                datasets will produce identical output.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function VintageRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null;
  hint: string;
}) {
  const missing = value === null;
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <dt className="text-xs font-medium text-gray-700">{label}</dt>
        <dd className="text-[10px] text-gray-500 leading-tight">{hint}</dd>
      </div>
      <span
        className={`shrink-0 text-xs font-mono ${
          missing ? 'text-gray-300 italic' : 'text-gray-900'
        }`}
      >
        {missing ? 'not ingested' : value}
      </span>
    </div>
  );
}
